import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { doc, getDoc } from 'firebase/firestore';

/**
 * ExamIntro
 *
 * Pre-exam landing screen. Explains the four sections, the rules, the
 * scoring threshold, the two-swing rule, and the cooldown logic. Also
 * gates access: participants must have a completed post-assessment
 * before they can start.
 *
 * If the post assessment is missing, this screen blocks entry and tells
 * them what to do.
 *
 * If the user is already in cooldown, redirects to the cooldown screen
 * by setting onNav('exam-cooldown').
 *
 * If the user has already passed, shows a celebration card and points
 * them to Account Settings for their certificate.
 */

export default function ExamIntro({ user, onNav, onStart }) {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [assessmentData, setAssessmentData] = useState(null);
  const [cooldown, setCooldown] = useState(null);
  const [attemptCount, setAttemptCount] = useState(0);
  const [latestResult, setLatestResult] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [userSnap, assessSnap, cooldownSnap, resultSnap] = await Promise.all([
          getDoc(doc(db, 'users', user.uid)),
          getDoc(doc(db, 'assessments', user.uid)),
          getDoc(doc(db, 'examCooldowns', user.uid)),
          getDoc(doc(db, 'examResults', user.uid)),
        ]);

        if (userSnap.exists()) setProfile(userSnap.data());
        if (assessSnap.exists()) setAssessmentData(assessSnap.data());
        if (resultSnap.exists()) setLatestResult(resultSnap.data());

        if (cooldownSnap.exists()) {
          const cd = cooldownSnap.data();
          const now = Date.now();
          const cooldownUntil = cd.cooldownUntil?.toDate ? cd.cooldownUntil.toDate().getTime() : 0;
          if (cooldownUntil > now) {
            setCooldown({ until: cooldownUntil, consecutiveFails: cd.consecutiveFails || 0 });
          }
          setAttemptCount(cd.totalAttempts || 0);
        }
      } catch (err) {
        console.error('ExamIntro load error:', err);
      }
      setLoading(false);
    };
    load();
  }, [user.uid]);

  const firstName = profile?.firstName || (user.displayName ? user.displayName.split(' ')[0] : '');
  const postDone = !!(assessmentData?.post?.scores?.total);
  const examPassed = profile?.examPassed === true;

  if (loading) {
    return <div style={{ color: '#8A8A8A', fontSize: '14px', padding: '40px' }}>Loading...</div>;
  }

  // Already passed, show celebration card with result breakdown
  if (examPassed) {
    const sectionLabels = {
      rice: 'Role-Specific RICE Prompt',
      workflow: 'Workflow Redesign',
      roi: 'ROI Measurement',
      agent: 'Role-Specific Agent',
    };
    const sectionNumbers = { rice: '01', workflow: '02', roi: '03', agent: '04' };

    return (
      <div style={s.wrapper}>
        <div style={s.passedHeader}>
          <div style={s.dayTag}>Final Exam · Passed</div>
          <div style={s.title}>You already crushed it</div>
        </div>
        <div style={s.passedBody}>
          <div style={s.passedIcon}>✓</div>
          <div style={s.passedHeadline}>
            {firstName ? `${firstName}, you passed the Final Exam.` : 'You passed the Final Exam.'}
          </div>
          <div style={s.passedSub}>
            Your certificate is waiting in Account Settings. Go grab it, save it, share it. You earned it.
          </div>
          <div style={s.btnRow}>
            <button style={s.primaryBtn} onClick={() => onNav('account')}>
              Go to Account Settings
            </button>
            <button style={s.secondaryBtn} onClick={() => onNav('dashboard')}>
              Back to Dashboard
            </button>
          </div>
        </div>

        {latestResult && (
          <>
            {/* Score hero */}
            <div style={s.resultScoreHero}>
              <div style={s.resultScoreCircle}>
                <div style={s.resultScoreNum}>{latestResult.totalScore || 0}</div>
                <div style={s.resultScoreDenom}>of 100</div>
              </div>
              <div style={s.resultScoreRight}>
                <div style={s.resultScoreLabel}>Your Total Score</div>
                <div style={s.resultScoreMsg}>
                  {latestResult.overallNote || 'You demonstrated all four core competencies. Well done.'}
                </div>
              </div>
            </div>

            {/* Section breakdown */}
            <div style={s.resultSection}>
              <div style={s.resultSectionTitle}>Your Section Breakdown</div>
              <div style={s.resultBreakdownGrid}>
                {['rice', 'workflow', 'roi', 'agent'].map(secId => {
                  const score = latestResult.sectionScores?.[secId] || 0;
                  const isStrongest = secId === latestResult.strongestSection;
                  return (
                    <div key={secId} style={{
                      ...s.resultBdCard,
                      borderLeftColor: score >= 20 ? '#1a8a4a' : score >= 12 ? '#D4A847' : '#C0392B',
                    }}>
                      <div style={s.resultBdNumber}>{sectionNumbers[secId]}</div>
                      <div style={s.resultBdLabel}>{sectionLabels[secId]}</div>
                      <div style={s.resultBdScoreRow}>
                        <span style={s.resultBdScore}>{score}</span>
                        <span style={s.resultBdOf}>/25</span>
                        {isStrongest && <span style={s.resultBdStrongTag}>Strongest</span>}
                      </div>
                      <div style={s.resultBdBar}>
                        <div style={{
                          ...s.resultBdFill,
                          width: `${(score / 25) * 100}%`,
                          background: score >= 20 ? '#1a8a4a' : score >= 12 ? '#D4A847' : '#C0392B',
                        }}></div>
                      </div>
                      {latestResult.sectionFeedback?.[secId] && (
                        <div style={s.resultBdFeedback}>{latestResult.sectionFeedback[secId]}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // Currently in cooldown
  if (cooldown) {
    const hoursLeft = Math.max(0, Math.ceil((cooldown.until - Date.now()) / (1000 * 60 * 60)));
    const minutesLeft = Math.max(0, Math.ceil((cooldown.until - Date.now()) / (1000 * 60)));
    const timeStr = hoursLeft >= 1 ? `${hoursLeft} ${hoursLeft === 1 ? 'hour' : 'hours'}` : `${minutesLeft} ${minutesLeft === 1 ? 'minute' : 'minutes'}`;

    return (
      <div style={s.wrapper}>
        <div style={s.cooldownHeader}>
          <div style={s.dayTag}>Final Exam · Cooldown</div>
          <div style={s.title}>Take a beat</div>
        </div>
        <div style={s.cooldownBody}>
          <div style={s.cooldownIcon}>⏳</div>
          <div style={s.cooldownHeadline}>
            That is three swings today.
          </div>
          <div style={s.cooldownSub}>
            The exam is locked for the next {timeStr} so you can step away and come back fresh. Open your workbook. Look at the prompt library you built, the workflow you redesigned, the math you ran, the agent you specced. The answers are in there. You are closer than this score is making you feel. Come back when the timer clears.
          </div>
          <div style={s.btnRow}>
            <button style={s.secondaryBtn} onClick={() => onNav('workbooks')}>
              Open My Workbooks
            </button>
            <button style={s.secondaryBtn} onClick={() => onNav('dashboard')}>
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Post assessment not done, block entry
  if (!postDone) {
    return (
      <div style={s.wrapper}>
        <div style={s.blockedHeader}>
          <div style={s.dayTag}>Final Exam · Locked</div>
          <div style={s.title}>Finish your post-training assessment first</div>
        </div>
        <div style={s.blockedBody}>
          <div style={s.blockedIcon}>🔒</div>
          <div style={s.blockedHeadline}>
            The Final Exam unlocks after your post-training assessment.
          </div>
          <div style={s.blockedSub}>
            The post-training assessment captures where you ended up across all four readiness dimensions. The Final Exam is where you prove you can apply what you learned. They happen in that order, on purpose.
          </div>
          <div style={s.btnRow}>
            <button style={s.primaryBtn} onClick={() => onNav('assessment')}>
              Take the Post-Training Assessment
            </button>
            <button style={s.secondaryBtn} onClick={() => onNav('dashboard')}>
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Ready to go, show the intro
  return (
    <div style={s.wrapper}>
      <div style={s.header}>
        <div style={s.dayTag}>Final Exam</div>
        <div style={s.title}>The last thing standing between you and that certificate</div>
      </div>

      <div style={s.body}>
        <div style={s.openingCard}>
          <div style={s.openingHeadline}>
            {firstName ? `Hey ${firstName}.` : 'Hey there.'}
          </div>
          <div style={s.openingText}>
            You made it through ten days. Both assessments are in the books. Now we find out whether you can actually do the four things this program is built to teach. Not memorize. Do.
          </div>
        </div>

        <div style={s.sectionTitle}>Here is what is about to happen</div>

        <div style={s.cardGrid}>
          <div style={s.card}>
            <div style={s.cardNumber}>01</div>
            <div style={s.cardLabel}>Role-Specific RICE Prompt</div>
            <div style={s.cardText}>Write a real prompt you could use at work tomorrow morning. All four RICE elements present, specific to your role.</div>
          </div>
          <div style={s.card}>
            <div style={s.cardNumber}>02</div>
            <div style={s.cardLabel}>Workflow Redesign</div>
            <div style={s.cardText}>Walk through one workflow you redesigned in this program. Before, after, where AI sits, what could go sideways.</div>
          </div>
          <div style={s.card}>
            <div style={s.cardNumber}>03</div>
            <div style={s.cardLabel}>ROI Measurement</div>
            <div style={s.cardText}>Show the math on the impact. Before number, after number, annualized savings, how you measured it.</div>
          </div>
          <div style={s.card}>
            <div style={s.cardNumber}>04</div>
            <div style={s.cardLabel}>Role-Specific Agent</div>
            <div style={s.cardText}>Tell us about a role-specific agent you built or specced. What it does, who it is for, how you know it works.</div>
          </div>
        </div>

        <div style={s.rulesPanel}>
          <div style={s.rulesTitle}>The rules</div>
          <div style={s.ruleRow}>
            <div style={s.ruleDot}>•</div>
            <div><strong>No time limit.</strong> Take as long as you need. We are not measuring speed.</div>
          </div>
          <div style={s.ruleRow}>
            <div style={s.ruleDot}>•</div>
            <div><strong>Two swings per section.</strong> If your first answer is missing something, you get one follow-up to fill it in. After that we move on.</div>
          </div>
          <div style={s.ruleRow}>
            <div style={s.ruleDot}>•</div>
            <div><strong>Passing is 75 out of 100, with at least 12 of 25 on every section.</strong> You cannot skate by on three strong sections and one bombed one.</div>
          </div>
          <div style={s.ruleRow}>
            <div style={s.ruleDot}>•</div>
            <div><strong>Unlimited retakes, mostly.</strong> Three fails in a row triggers a 12 hour cooldown. Use the time to review.</div>
          </div>
          <div style={s.ruleRow}>
            <div style={s.ruleDot}>•</div>
            <div><strong>Pass and your certificate appears in Account Settings.</strong> That is the moment.</div>
          </div>
        </div>

        {attemptCount > 0 && (
          <div style={s.attemptNote}>
            This is attempt #{attemptCount + 1}. Bring everything you have.
          </div>
        )}

        <div style={s.startRow}>
          <button style={s.startBtn} onClick={onStart}>
            I am ready, start the exam →
          </button>
          <button style={s.cancelBtn} onClick={() => onNav('dashboard')}>
            Not yet, go back
          </button>
        </div>

        <div style={s.fineprint}>
          Once you start, the exam runs continuously through all four sections. Plan to give yourself 25 to 35 minutes of focused time. Your answers are saved as you go.
        </div>
      </div>
    </div>
  );
}

const s = {
  wrapper: { maxWidth: '880px', paddingBottom: '60px', fontFamily: 'DM Sans, sans-serif' },

  header: { background: '#1B2A4A', padding: '28px 32px', marginBottom: '0' },
  passedHeader: { background: '#1a8a4a', padding: '28px 32px', marginBottom: '0' },
  cooldownHeader: { background: '#8A6F2A', padding: '28px 32px', marginBottom: '0' },
  blockedHeader: { background: '#5A5A5A', padding: '28px 32px', marginBottom: '0' },

  dayTag: { fontSize: '10px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', color: '#D4A847', marginBottom: '6px' },
  title: { fontFamily: 'Playfair Display, serif', fontSize: '24px', color: '#fff', lineHeight: '1.3' },

  body: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', borderTop: 'none', padding: '32px' },

  openingCard: { background: '#F2F0EC', padding: '24px 28px', marginBottom: '32px', borderLeft: '4px solid #D4A847' },
  openingHeadline: { fontFamily: 'Playfair Display, serif', fontSize: '22px', color: '#1B2A4A', marginBottom: '10px' },
  openingText: { fontSize: '14px', color: '#3D3D3D', lineHeight: '1.7', fontWeight: '300' },

  sectionTitle: { fontFamily: 'Playfair Display, serif', fontSize: '18px', color: '#1B2A4A', marginBottom: '16px', paddingBottom: '8px', borderBottom: '2px solid #E8E6E2' },

  cardGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px', marginBottom: '32px' },
  card: { background: '#FAF8F5', border: '1.5px solid #E8E6E2', padding: '20px', borderRadius: '0', position: 'relative' },
  cardNumber: { fontFamily: 'Playfair Display, serif', fontSize: '22px', fontWeight: '700', color: '#D4A847', marginBottom: '8px' },
  cardLabel: { fontFamily: 'Playfair Display, serif', fontSize: '14px', fontWeight: '700', color: '#1B2A4A', marginBottom: '8px', lineHeight: '1.3' },
  cardText: { fontSize: '12px', color: '#3D3D3D', lineHeight: '1.6', fontWeight: '300' },

  rulesPanel: { background: '#1B2A4A', padding: '24px 28px', marginBottom: '24px' },
  rulesTitle: { fontFamily: 'Playfair Display, serif', fontSize: '16px', color: '#D4A847', marginBottom: '14px', letterSpacing: '1px', textTransform: 'uppercase' },
  ruleRow: { display: 'flex', gap: '12px', marginBottom: '12px', fontSize: '13px', color: 'rgba(255,255,255,0.85)', lineHeight: '1.6', fontWeight: '300' },
  ruleDot: { color: '#D4A847', fontWeight: '700', flexShrink: 0 },

  attemptNote: { background: '#FFF8E5', border: '1.5px solid #D4A847', padding: '12px 16px', fontSize: '13px', color: '#3D3D3D', marginBottom: '24px', fontStyle: 'italic' },

  startRow: { display: 'flex', gap: '12px', marginBottom: '16px' },
  startBtn: { background: '#1B2A4A', color: '#fff', border: 'none', padding: '14px 32px', fontFamily: 'Playfair Display, serif', fontSize: '15px', fontWeight: '700', cursor: 'pointer', letterSpacing: '0.5px' },
  cancelBtn: { background: 'transparent', color: '#8A8A8A', border: '1.5px solid #E8E6E2', padding: '14px 24px', fontFamily: 'Playfair Display, serif', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
  fineprint: { fontSize: '12px', color: '#8A8A8A', fontStyle: 'italic', lineHeight: '1.6', borderTop: '1px solid #E8E6E2', paddingTop: '14px' },

  // Passed state
  passedBody: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', borderTop: 'none', padding: '40px', textAlign: 'center' },
  passedIcon: { fontFamily: 'Playfair Display, serif', fontSize: '64px', color: '#1a8a4a', marginBottom: '16px', lineHeight: '1' },
  passedHeadline: { fontFamily: 'Playfair Display, serif', fontSize: '22px', color: '#1B2A4A', marginBottom: '12px' },
  passedSub: { fontSize: '14px', color: '#3D3D3D', lineHeight: '1.7', marginBottom: '28px', maxWidth: '480px', margin: '0 auto 28px' },

  // Cooldown state
  cooldownBody: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', borderTop: 'none', padding: '40px', textAlign: 'center' },
  cooldownIcon: { fontSize: '52px', marginBottom: '16px' },
  cooldownHeadline: { fontFamily: 'Playfair Display, serif', fontSize: '22px', color: '#1B2A4A', marginBottom: '14px' },
  cooldownSub: { fontSize: '14px', color: '#3D3D3D', lineHeight: '1.7', marginBottom: '28px', maxWidth: '560px', margin: '0 auto 28px' },

  // Blocked state
  blockedBody: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', borderTop: 'none', padding: '40px', textAlign: 'center' },
  blockedIcon: { fontSize: '52px', marginBottom: '16px' },
  blockedHeadline: { fontFamily: 'Playfair Display, serif', fontSize: '20px', color: '#1B2A4A', marginBottom: '14px' },
  blockedSub: { fontSize: '14px', color: '#3D3D3D', lineHeight: '1.7', marginBottom: '28px', maxWidth: '560px', margin: '0 auto 28px' },

  btnRow: { display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' },
  primaryBtn: { background: '#1B2A4A', color: '#fff', border: 'none', padding: '12px 26px', fontFamily: 'Playfair Display, serif', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
  secondaryBtn: { background: 'transparent', color: '#1B2A4A', border: '1.5px solid #1B2A4A', padding: '12px 26px', fontFamily: 'Playfair Display, serif', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },

  // Result display styles (shown below celebration when user has passed)
  resultScoreHero: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', borderTop: 'none', padding: '28px 32px', display: 'flex', alignItems: 'center', gap: '24px' },
  resultScoreCircle: {
    width: '120px',
    height: '120px',
    borderRadius: '50%',
    background: '#1B2A4A',
    border: '4px solid #D4A847',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  resultScoreNum: { fontFamily: 'Playfair Display, serif', fontSize: '44px', fontWeight: '900', color: '#fff', lineHeight: '1' },
  resultScoreDenom: { fontSize: '10px', color: '#D4A847', letterSpacing: '1.5px', textTransform: 'uppercase', marginTop: '4px' },
  resultScoreRight: { flex: 1 },
  resultScoreLabel: { fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase', color: '#8A8A8A', marginBottom: '8px', fontWeight: '600' },
  resultScoreMsg: { fontFamily: 'Playfair Display, serif', fontSize: '15px', color: '#1B2A4A', lineHeight: '1.6', fontWeight: '400' },

  resultSection: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', borderTop: 'none', padding: '24px 32px' },
  resultSectionTitle: { fontFamily: 'Playfair Display, serif', fontSize: '17px', color: '#1B2A4A', marginBottom: '16px', paddingBottom: '8px', borderBottom: '2px solid #E8E6E2' },
  resultBreakdownGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' },
  resultBdCard: { background: '#FAF8F5', borderLeft: '4px solid #E8E6E2', padding: '16px 18px', position: 'relative' },
  resultBdNumber: { fontFamily: 'Playfair Display, serif', fontSize: '16px', fontWeight: '700', color: '#D4A847', marginBottom: '4px' },
  resultBdLabel: { fontFamily: 'Playfair Display, serif', fontSize: '13px', fontWeight: '700', color: '#1B2A4A', marginBottom: '8px' },
  resultBdScoreRow: { display: 'flex', alignItems: 'baseline', gap: '6px', marginBottom: '10px', flexWrap: 'wrap' },
  resultBdScore: { fontFamily: 'Playfair Display, serif', fontSize: '28px', fontWeight: '900', color: '#1B2A4A', lineHeight: '1' },
  resultBdOf: { fontSize: '12px', color: '#8A8A8A' },
  resultBdStrongTag: { fontSize: '9px', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', background: '#1a8a4a', color: '#fff', padding: '3px 8px', marginLeft: 'auto' },
  resultBdBar: { height: '5px', background: '#E8E6E2', marginBottom: '10px', overflow: 'hidden' },
  resultBdFill: { height: '100%', transition: 'width 0.6s ease-out' },
  resultBdFeedback: { fontSize: '11px', color: '#3D3D3D', lineHeight: '1.5', fontWeight: '300', fontStyle: 'italic' },
};
